import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../config/api';
// import { request } from 'http';
// import { workerData } from 'worker_threads';


// REACT_APP_API_TAXES_KEY from .env file name ninjas-api-key
const apiKey = process.env.REACT_APP_API_TAXES_KEY;


interface ErrorType {
  message: string; // Adjust this based on the actual error structure you expect
}

export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (
    {
      clientFirstName = '',
      clientLastName = '',
      deliveryAddress = '',
      deliveryCity = '',
      deliveryState = '',
      deliveryPostalCode = '',
      clientId = '',
      salesmanId = '',
      installerId = '',
      status = '',
      excludeChanged = false,
      includeInvoices = true,
      startDate = '',
      endDate = '',
      sortColumn = 'createdAt',
      sortOrder = 'DESC',
      pageSize = 500,
      currentPage = 1,
    }: {
      clientFirstName?: string;
      clientLastName?: string;
      deliveryAddress?: string;
      deliveryCity?: string;
      deliveryState?: string;
      deliveryPostalCode?: string;
      clientId?: string;
      salesmanId?: string;
      installerId?: string;
      status?: string;
      excludeChanged?: boolean;
      includeInvoices?: boolean;
      startDate?: string;
      endDate?: string;
      sortColumn?: 'firstName' | 'lastName' | 'email' | 'createdAt';
      sortOrder?: 'ASC' | 'DESC';
      pageSize?: number;
      currentPage?: number;
    } = {},
    { rejectWithValue }: { rejectWithValue: any; getState: any; dispatch: any }
  ) => {
    try {
      let query = '/Orders?';
      if (pageSize) query += `&pageSize=${pageSize}`;
      if (sortOrder) query += `&sortOrder=${sortOrder}`;
      if (sortColumn) query += `&sortColumn=${sortColumn}`;
      if (currentPage) query += `&page=${currentPage}`;
      if (clientFirstName) query += `&clientFirstName=${encodeURIComponent(clientFirstName)}`;
      if (clientLastName) query += `&clientLastName=${encodeURIComponent(clientLastName)}`;
      if (deliveryAddress) query += `&deliveryAddress=${encodeURIComponent(deliveryAddress)}`;
      if (deliveryCity) query += `&deliveryCity=${encodeURIComponent(deliveryCity)}`;
      if (deliveryState) query += `&deliveryState=${encodeURIComponent(deliveryState)}`;
      if (deliveryPostalCode) query += `&deliveryPostalCode=${encodeURIComponent(deliveryPostalCode)}`;
      if (clientId) query += `&clientId=${encodeURIComponent(clientId)}`;
      if (salesmanId) query += `&salesmanId=${encodeURIComponent(salesmanId)}`;
      if (installerId) query += `&installerId=${encodeURIComponent(installerId)}`;
      if (status) query += `&status=${encodeURIComponent(status)}`;
      if (excludeChanged) query += `&excludeChanged=${excludeChanged}`;
      if (includeInvoices) query += `&includeInvoices=${includeInvoices}`;
      if (startDate) query += `&startDate=${encodeURIComponent(startDate)}`;
      if (endDate) query += `&endDate=${encodeURIComponent(endDate)}`;

      const response = await api.get(query);

      // If the token is expired, refresh it

      return response.data;
    } catch (error: any) {

      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);


interface Tokens {
  token: string;
  refreshToken: string;
}

export const saveTokens = createAsyncThunk<Tokens, Tokens>(
  'auth/saveTokens',
  async ({ token, refreshToken }: Tokens) => {
    return { token, refreshToken };
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/Orders/${orderId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);


interface UpdateOrderStatusPayload {
  status: number;
  orderId: number;
}

export const updateOrderStatus = createAsyncThunk(
  'orders/updateOrderStatus',
  async ({ status, orderId }: UpdateOrderStatusPayload, { rejectWithValue }) => {
    try {
      const response = await api.put(`/Orders/${orderId}/Status`, { status });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
// Update installerId /orders/{orderId}/installer/{userid} PUT Authorization: Bearer {token}

interface UpdateInstallerIdPayload {
  orderId: string;
  installerId: string;
}

export const updateInstallerId = createAsyncThunk(
  'orders/updateInstallerId',
  async ({ orderId, installerId }: UpdateInstallerIdPayload, { rejectWithValue }) => {
    try {
      const payload = {
        orderId: orderId,
        installerId: installerId
      };
      const response = await api.post(`/Orders/${orderId}/Installer/${installerId}`, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);


// Define the initial state of the orders slice
interface OrderPayload {
  clientId: string;
  installerId: string; // added this field
  salesmanId: string;
  isVerticalRoof: boolean; // added this field
  commission: number;
  transportationFee: number;
  drawingsAmount: number;
  equipmentAmount: number;
  discountAmount: number;
  installationAmount: number;
  reference: number;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  source: string;
  items: [
    {
      description: string;
      materialId: number;
      quantity: number;
      unitPrice: number;
      metadata: {
        [key: string]: string;
      };
    }
  ];
  metadata: {
    [key: string]: string;
  };
}

export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData: OrderPayload, { rejectWithValue }) => {
    try {
      // Validation for source removed as it is not in the required API payload
      const response = await api.post('/Orders', orderData);
      console.log(response.data);
      return response.data;
    } catch (error: any) {
      console.log(error.response?.data || error);
      return rejectWithValue(error.response?.data?.message || error.message || 'An unknown error occurred');
    }
  }
);


// Order notes


interface NotesPayload {

  id: string;
  notes: { userId: string; createdAt: string; text: string; attachments: { description: string; url: string }[]; file: string }[]
}

export const updateOrdersNotes = createAsyncThunk(
  'Orders/updateOrdersNotes',
  async (notesData: NotesPayload, { rejectWithValue }) => {
    try {
      const response = await api.put(`/Orders/${notesData.id}/Notes`, notesData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);


// Permits
//  Create permit /Permits POST Authorization: Bearer {token} {
//   "orderId": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
//   "details": "string",
//   "status": 0
// }

interface CreatePermitPayload {
  orderId: string;
  details: string;
  status: number;
}

export const createPermit = createAsyncThunk(
  'Orders/createPermit',
  async (permitData: CreatePermitPayload, { rejectWithValue }) => {
    try {
      const response = await api.post('/Permits', permitData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);



// Update permit /Permits/{permitId} PUT Authorization: Bearer {token}


interface PermitPayload {
  id: string;
  details: string;
  status: number;
}

export const updateOrdersPermit = createAsyncThunk(
  'Orders/updateOrdersPermit',
  async (permitData: PermitPayload, { rejectWithValue }) => {
    try {
      const response = await api.put(`/Permits/${permitData.id}`, {
        details: permitData.details,
        status: permitData.status
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Create work order /WorkOrder POST Authorization: Bearer {token} POST /WorkOrder 

// Request
// {
//          "OrderId":"string",
//          "InstallerId": "string",
//          "CheckList":[
//           "Description":"string",
//           "Done":boolean
//          ]
// //   }
// and update Authorization: Bearer {token} POST /WorkOrder/{id} request
// {
//   "CheckList":[
//   "Description":"string",
//    "Done":boolean
//    ],
//    "Status": 0
//   }

//   // status NotReady (0)

// Ready (1)

// workerData

// interface WorkOrderPayload {
//   OrderId: string;
//   InstallerId: string;
//   CheckList: { Description: string; Done: boolean }[];
// }



export const createWorkOrderOS = createAsyncThunk(
  'Orders/createWorkOrder',
  async (WorkOrderPayload: any, { rejectWithValue }) => {
    try {
      const response = await api.post('/WorkOrder', WorkOrderPayload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);


interface UpdateWorkOrderPayload {
  id: string;
  CheckList: { Description: string; Done: boolean }[];
  Status: number;
}

export const updateWorkOrderOS = createAsyncThunk(
  'Orders/updateWorkOrder',
  async (WorkOrderPayload: UpdateWorkOrderPayload, { rejectWithValue }) => {
    try {
      const response = await api.put(`/WorkOrder/${WorkOrderPayload.id}`, WorkOrderPayload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

// create Slice to get the right taxes https://api.api-ninjas.com/v1/salestax?zip_code={zip_code} and header X-Api-Key comming from apiKey slice name taxesAPI 

export const fetchTaxes = createAsyncThunk(
  'orders/fetchTaxes',
  async (zipCode: number, { rejectWithValue }) => {
    try {
      const response = await axios.get(`https://api.api-ninjas.com/v1/salestax?zip_code=${zipCode}`, {
        headers: {
          'X-Api-Key': apiKey,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);






const ordersSlice = createSlice({
  name: 'orders',
  initialState: {
    items: [],
    orders: {
      items: [],
      status: 'idle',
      error: null,
      notes: [],
      permit: [],
    },
    workOrder: {
      items: [],
      status: 'idle',
      error: null,
    },
    taxes: {},
    order: null as OrderPayload | null,
    status: 'idle', // Legacy/Backwards compatibility
    listStatus: 'idle',
    detailsStatus: 'idle',
    saveStatus: 'idle',
    taxesStatus: 'idle',
    error: null as ErrorType | null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.listStatus = 'loading';
        state.status = 'loading';
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.status = 'succeeded';
        state.items = action.payload.items;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.status = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      })
      .addCase(fetchOrderById.pending, (state) => {
        state.detailsStatus = 'loading';
        // Clear stale order so the useEffect in OrderDetails doesn't fire
        // with the wrong order's data. The UI gates on orderLoaded
        // (orderDetails.id === prop.id) and shows a spinner until the correct
        // order arrives, so clearing here is safe.
        state.order = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.detailsStatus = 'succeeded';
        state.status = 'succeeded';
        state.order = action.payload; // Set the selected order
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.detailsStatus = 'failed';
        state.status = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      })
      .addCase(updateOrderStatus.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        state.order = action.payload;
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      })
      .addCase(createOrder.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        state.order = action.payload;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      })
      .addCase(updateOrdersNotes.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(updateOrdersNotes.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        state.orders.notes = action.payload;
      })
      .addCase(updateOrdersNotes.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      })
      .addCase(updateOrdersPermit.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(updateOrdersPermit.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        state.orders.permit = action.payload;
      })
      .addCase(updateOrdersPermit.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      })
      .addCase(updateInstallerId.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(updateInstallerId.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        state.order = action.payload;
      })
      .addCase(updateInstallerId.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      })
      .addCase(createPermit.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(createPermit.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        state.orders.permit = action.payload;
      })
      .addCase(createPermit.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      })
      .addCase(createWorkOrderOS.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(createWorkOrderOS.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        state.workOrder.items = action.payload;
      })
      .addCase(createWorkOrderOS.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      })
      .addCase(updateWorkOrderOS.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(updateWorkOrderOS.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        state.status = 'succeeded';
        state.workOrder.items = action.payload;
      })
      .addCase(updateWorkOrderOS.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.status = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      })
      .addCase(fetchTaxes.pending, (state) => {
        state.taxesStatus = 'loading';
      })
      .addCase(fetchTaxes.fulfilled, (state, action) => {
        state.taxesStatus = 'succeeded';
        state.taxes = action.payload;
      })
      .addCase(fetchTaxes.rejected, (state, action) => {
        state.taxesStatus = 'failed';
        state.error = action.error.message ? { message: action.error.message } : null;
      });

  },
});

export default ordersSlice.reducer;





